import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useFeatureFlagsStore, selectFeatureFlagsFingerprint } from '../../stores/featureFlagsStore';
import { useAuthStore } from '../../stores/authStore';
import { useAccessControlStore } from '../../stores/accessControlStore';
import { useEffectiveAccess } from '../../stores/accessControlStore';

interface FeatureGateProps {
  path: string;
  children: ReactNode;
}

/**
 * Renders children only if the feature flag for the given path is enabled
 * and (when ACL-restricted) the user has the matching module permission.
 * Otherwise redirects to the first enabled tab (or /settings) to avoid loops when
 * Command Center is disabled.
 */
const FeatureGate: React.FC<FeatureGateProps> = ({ path, children }) => {
  const user = useAuthStore((s) => s.user);
  const isStudent = Boolean(user?.roles?.includes('student'));
  const effectiveAccess = useEffectiveAccess();
  const canPath = useAccessControlStore((s) => s.canPath);

  const { isPathEnabled, getFirstEnabledFallbackPath } = useFeatureFlagsStore(
    useShallow((s) => ({
      isPathEnabled: s.isPathEnabled(path),
      flagsFingerprint: selectFeatureFlagsFingerprint(s),
      getFirstEnabledFallbackPath: s.getFirstEnabledFallbackPath,
    }))
  );

  const aclAllowed = path === '/settings' || canPath(path);

  if (!isPathEnabled || !aclAllowed) {
    let fallbackTo = getFirstEnabledFallbackPath({ student: isStudent });
    // Prefer a fallback the user can actually open under ACL
    if (!canPath(fallbackTo) && fallbackTo !== '/settings') {
      fallbackTo = '/settings';
    }
    if (fallbackTo === path) {
      fallbackTo = getFirstEnabledFallbackPath({ student: false });
      if (!canPath(fallbackTo) && fallbackTo !== '/settings') {
        fallbackTo = '/settings';
      }
    }
    // Avoid redirect loops when ACL blocks everything except settings
    if (fallbackTo === path) {
      fallbackTo = '/settings';
    }
    return <Navigate to={fallbackTo} replace />;
  }
  // Touch effectiveAccess so React tracks it for re-render
  void effectiveAccess;
  return <>{children}</>;
};

export default FeatureGate;
